import React from 'react';
import uuid from 'uuid-random';
import { RouteComponentProps, useLocation, useParams } from '@reach/router';
import { useState, useEffect } from 'react';
import { lessonsApi } from '../../API/lessons';
import { CollectionData } from '../../types';
import {
  LessonTypeWrapper,
  LessonTypeHeader,
  LessonTypesContainer,
  TextTypeRow,
  LessonsSubHeader,
  LessonsContainer,
  StyledCenteredSpinnerWrapper,
} from '../Styled/StyledWrappers';
import { StyledColoredLine } from '../Styled';
import { TextTypeStyledLink } from '../Styled/TextTypeStyledLink';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { ContactUsError } from '../ContactUsError';
import { LessonsHeaderContainer } from '../LessonsHeader/LessonsHeader';
import { lessonsNavLinks } from '../../pages/Lessons/config';
import { BookLabelName } from '../../types/exemplars';
import { getLabelBgColor, getLabelLetterIcon, getLabelTextColor, getTopLineBgColor } from '../../utils';
import { COLORS } from '../../constants';
import { ComingSoon } from '../ComingSoon';
import { getLessonAvailability } from '../../utils/envs';

const getRows = (arr: Array<CollectionData>, rowSize: number) => {
  const rows = [];
  for (let i = 0, j = arr.length; i < j; i += rowSize) {
    const chunk = arr.slice(i, i + rowSize);
    const row = (
      <TextTypeRow key={uuid()}>
        {chunk.map(type => (
          <TextTypeStyledLink
            aria-label={type.textTypeName}
            tabIndex={0}
            key={type.exemplarId}
            to={`exemplarsRelated/${type.exemplarId}/textType/${type.textTypeId}`}
            data-testid="lessonPlan"
          >
            {type.textTypeName.toUpperCase()}
          </TextTypeStyledLink>
        ))}
      </TextTypeRow>
    );
    rows.push(row);
  }
  return rows;
};

export const LessonPlan: React.FC<RouteComponentProps> = (): JSX.Element => {
  const { pathname } = useLocation();
  const params = useParams();

  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [plans, setPlans] = useState<CollectionData[]>([]);
  const [backLinkNames, setBackLinkNames] = useState<string[]>([]);
  const [title, setTitle] = useState<BookLabelName>(BookLabelName.default);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { data },
        } = await lessonsApi.getCollection(params.collectionId);

        const firstCollectionName = data[0].collectionName;
        setIsComingSoon(!getLessonAvailability(firstCollectionName.split(/(\s+)/)[0]));
        setPlans(data);
        setBackLinkNames(['Find a lesson plan', firstCollectionName]);
        setTitle(firstCollectionName);
      } catch (error) {
        setError(!!error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const letterColor = title ? getLabelTextColor(title) : '';
  const backgroundColor = title ? getLabelBgColor(title) : '';
  const [linear1, linear2, linear3] = title ? getTopLineBgColor(title) : [];
  const Letter = title ? getLabelLetterIcon(title) : () => null;

  if (isComingSoon) {
    return <ComingSoon stageNotReady />;
  }

  if (isLoading) {
    return (
      <StyledCenteredSpinnerWrapper behavior={FlexBehavior.container} justify={FlexJustify.center}>
        <Spinner color={COLORS.seaBlue} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  } else if (error) {
    return <ContactUsError />;
  }
  return (
    <>
      <StyledColoredLine linearColor1={linear1} linearColor2={linear2} linearColor3={linear3} />
      <LessonsContainer width={959}>
        <LessonsHeaderContainer
          navLinks={lessonsNavLinks.filter(link => pathname.match(link.regExp))}
          params={{
            ...params,
            names: backLinkNames,
          }}
        />
        <LessonsSubHeader tabIndex={-1}>Select a text type</LessonsSubHeader>
        <LessonTypeWrapper>
          <LessonTypeHeader letterColor={letterColor} backgroundColor={backgroundColor}>
            {title.substr(0, title.length - 2)} <Letter />
          </LessonTypeHeader>
          <LessonTypesContainer>{getRows(plans, 3)}</LessonTypesContainer>
        </LessonTypeWrapper>
      </LessonsContainer>
    </>
  );
};
